// English translations
export const en = {
  common: {
    dashboard: "Dashboard",
    analytics: "Analytics",
    leaderboard: "Leaderboard",
    profile: "Profile",
    settings: "Settings",
    chatbot: "AI Assistant",
    login: "Login",
    logout: "Logout",
    register: "Register",
    household: "Household",
    agriculture: "Agriculture",
    darkMode: "Dark Mode",
    lightMode: "Light Mode",
    language: "Language",
    notifications: "Notifications",
    save: "Save",
    cancel: "Cancel",
    email: "Email",
    password: "Password",
    name: "Name",
    welcomeBack: "Welcome back",
    createAccount: "Create an account",
    forgotPassword: "Forgot password?",
    alreadyHaveAccount: "Already have an account?",
    signIn: "Sign in",
    signUp: "Sign up",
    errorOccurred: "An error occurred",
    success: "Success",
    loading: "Loading...",
    logoutConfirmation: "Are you sure you want to log out?",
    logoutSuccess: "Logged out successfully",
    logoutSuccessMessage: "You have been logged out of your account",
    switchedToAgriculture: "Switched to Agriculture Mode",
    switchedToHousehold: "Switched to Household Mode",
    switchToAgriculture: "Switch to Agriculture",
    switchToHousehold: "Switch to Household",
    dashboardUpdated: "Your dashboard and insights have been updated.",
    toggleSidebar: "Toggle sidebar",
    toggleTheme: "Toggle theme",
    appName: "AQUAGENIUS",
    logoAlt: "AquaGenius Logo",
    days: {
      sun: "Sun",
      mon: "Mon",
      tue: "Tue",
      wed: "Wed",
      thu: "Thu",
      fri: "Fri",
      sat: "Sat"
    },
    assistant: "AI Assistant",
    typeMessage: "Type a message...",
    sending: "Sending",
    error: "Error",
    errorMessage: "Sorry, I couldn't process your request. Please try again.",
    weather: {
      currentConditions: "Current conditions",
      tomorrowForecast: "Tomorrow's forecast",
      temperature: "Temperature",
      humidity: "Humidity",
      rainfall: "Rainfall",
      irrigationAdvice: "Irrigation advice",
      adjustSchedule: "Adjust schedule",
      showForecast: "Show forecast",
      getEfficiencyTips: "Get efficiency tips",
      normalConditions: "Normal conditions. Follow regular irrigation schedule.",
      highTemperature: "High temperature detected. Consider evening irrigation to minimize evaporation.",
      highHumidity: "High humidity detected. Reduce irrigation to prevent fungal growth.",
      currentRain: "Currently raining. No irrigation needed.",
      expectedRain: "Rain expected tomorrow. Consider delaying irrigation."
    },
    locationNeeded: "Location Access Needed",
    locationDescription: "Enable location access to get personalized weather-based recommendations",
    enableLocation: "Enable Location",
    locationDisabled: "Location access disabled",
    typing: "Assistant is typing"
  },
  dashboard: {
    waterUsage: "Water Usage",
    totalSaved: "Total Saved",
    savings: "Savings",
    efficiency: "Efficiency",
    weeklyReport: "Weekly Report",
    tipOfDay: "Tip of the Day",
    leaks: "Potential Leaks",
    insights: "Insights",
    todayUsage: "Today's Usage",
    compareToYesterday: "Compared to yesterday",
    household: {
      title: "Household Water Dashboard",
      description: "Monitor your household water usage, track savings, and receive AI-powered optimization tips.",
      dailyUsage: "Daily Usage",
      monthlyUsage: "Monthly Usage",
      efficiencyScore: "Efficiency Score",
      usageTrends: "Water Usage Trends"
    },
    agriculture: {
      title: "Agricultural Water Dashboard",
      description: "Monitor your agricultural water usage, track irrigation efficiency, and receive crop-specific recommendations.",
      dailyIrrigation: "Daily Irrigation",
      monthlyIrrigation: "Monthly Irrigation",
      irrigationEfficiency: "Irrigation Efficiency",
      irrigationTrends: "Irrigation Trends"
    }
  },
  settings: {
    title: "Settings",
    account: "Account",
    notifications: "Notifications",
    devices: "Devices",
    privacy: "Privacy",
    billing: "Billing",
    help: "Help",
    profileInfo: "Profile Information",
    updateDetails: "Update your account details",
    pwdChange: "Change your password",
    notifPrefs: "Notification Preferences",
    connectedDevices: "Connected Devices",
    privacySettings: "Privacy Settings",
    billingInfo: "Billing Information",
    faq: "Frequently Asked Questions",
    languageSettings: "Language Settings",
    chooseLanguage: "Choose your preferred language and text direction",
    selectLanguage: "Select Language",
    textDirection: "Text Direction",
    textDirectionAuto: "Text direction is automatically set based on the selected language",
    rightToLeft: "Right to Left (RTL)",
    leftToRight: "Left to Right (LTR)",
    current: "Current",
    profileInformation: "Profile Information",
    updateAccountDetails: "Update your account details",
    waterUsageAlerts: "Water Usage Alerts",
    dailyUsageSummary: "Daily Usage Summary",
    dailyUsageSummaryDesc: "Receive a daily summary of your water usage",
    manageNotifications: "Manage how you receive notifications",
    manageDevices: "Manage your water monitoring devices",
    mainWaterMeter: "Main Water Meter",
    connectedSince: "Connected since {{date}}"
  },
  chatbot: {
    placeholder: "Ask me anything about water conservation...",
    suggestions: [
      "How can I reduce my water usage?",
      "Do I have any leaks?",
      "Show me water-saving tips",
      "What's my water usage trend?"
    ],
    welcome: "Hi there! I'm AquaGenius AI. How can I help you save water today?",
    suggestedQuestions: "Suggested questions:",
    voiceRecording: {
      started: "Voice recording started",
      stopped: "Voice recording stopped",
      completed: "Voice recognition complete"
    },
    responses: {
      usage: "Based on your data, you're using about 20% more water in the kitchen than similar households. Consider installing a low-flow faucet aerator to save up to 700 gallons annually.",
      leak: "I've noticed an unusual water usage pattern between 2-4 AM for the past three days. This could indicate a bathroom leak. Would you like me to provide diagnostic steps?",
      plan: "I've created an optimized water supply plan for you! Moving garden irrigation to morning hours can save up to 30% water due to reduced evaporation. Would you like to see the full optimization report?",
      general: [
        "Did you know that fixing a leaky faucet can save up to 3,000 gallons annually? I can help you identify potential leaks in your home.",
        "Based on your usage pattern, water consumption can be reduced by 15% through shorter showers. Would you like some tips on efficient shower habits?",
        "Your garden irrigation seems inefficient compared to similar households. Consider installing a drip irrigation system to save up to 80% water.",
        "I notice you're using a lot of water during peak hours. Moving some activities to off-peak hours can save money and reduce strain on water systems."
      ]
    },
    ui: {
      assistant: "AquaGenius Assistant",
      openChat: "Open chat assistant",
      minimize: "Minimize",
      maximize: "Maximize",
      close: "Close"
    }
  },
  insights: {
    title: "AI Insights & Tips",
    waterTips: "Water-Saving Tips",
    analysis: "Weather Analysis",
    waterAnalysis: "Water Usage Analysis",
    exportReport: "Export Report",
    shareInsights: "Share Insights",
    setSchedule: "Set Schedule",
    setAlert: "Set Alert",
    viewDetails: "View Details",
    downloadData: "Download Data",
    viewGuide: "View Guide",
    setReminder: "Set Reminder",
    learnMore: "Learn More",
    getAlerts: "Get Alerts",
    weather: {
      temperature: "Temperature",
      humidity: "Humidity",
      rainfall: "Rainfall",
      waterEfficiency: "Water Efficiency",
      feelsLike: "Feels like",
      last24Hours: "Last 24 hours",
      basedOnConditions: "Based on current conditions",
      forecast: "5-Day Forecast",
      waterManagement: "Water Management Advice",
      recommendations: "Recommendations",
      conditions: {
        high: "High",
        low: "Low",
        normal: "Normal"
      }
    },
    agricultureTips: {
      irrigation: "Smart Irrigation Scheduling",
      soil: "Soil Moisture Management",
      bestTime: "Best irrigation time",
      soilMoisture: "Soil moisture retention",
      waterStress: "Risk of water stress",
      times: {
        early: "Early morning or late evening",
        mid: "Mid-morning"
      },
      status: {
        good: "Good",
        monitor: "Monitor closely",
        high: "High",
        low: "Low"
      }
    },
    householdTips: {
      daily: "Daily Water Conservation",
      smart: "Smart Water Usage",
      indoor: "Indoor humidity",
      garden: "Garden watering",
      conservation: "Water conservation",
      status: {
        dehumidifier: "Consider dehumidifier",
        normal: "Normal range",
        skip: "Skip today",
        proceed: "Proceed as scheduled",
        important: "Important",
        standard: "Standard measures"
      }
    },
    agricultureDescription: "Smart irrigation recommendations based on weather data",
    householdDescription: "Water conservation tips for your home",
    exportSuccess: "Report exported successfully",
    exportError: "Failed to export report",
    shareTitle: "AQUA Water Management Insights",
    shareText: "Check out these water management insights from AQUA!",
    shareSuccess: "Insights shared successfully",
    shareError: "Failed to share insights",
    copySuccess: "Link copied to clipboard",
    error: {
      title: "Error Loading Insights",
      description: "Failed to load weather data. Please try again later."
    },
    waterAdvice: {
      agriculture: {
        heavyRain: "Heavy rainfall detected. Skip irrigation for today.",
        highTempLowHumidity: "High temperature and low humidity. Consider drip irrigation to minimize water loss.",
        rainTomorrow: "Significant rain expected tomorrow. Delay irrigation if possible.",
        highHumidity: "High humidity levels. Monitor for fungal diseases and reduce irrigation.",
        normal: "Normal conditions. Follow regular irrigation schedule."
      },
      household: {
        rainCollection: "Good day to collect rainwater for garden use.",
        hotDay: "Hot day ahead. Consider watering plants early morning or late evening.",
        rainTomorrow: "Rain expected tomorrow. Hold off on watering outdoor plants.",
        normal: "Normal conditions. Follow regular watering schedule."
      }
    }
  },
};

// French translations
export const fr = {
  common: {
    dashboard: "Tableau de bord",
    analytics: "Analyses",
    leaderboard: "Classement",
    profile: "Profil",
    settings: "Paramètres",
    chatbot: "Assistant IA",
    login: "Connexion",
    logout: "Déconnexion",
    register: "S'inscrire",
    household: "Ménage",
    agriculture: "Agriculture",
    darkMode: "Mode sombre",
    lightMode: "Mode clair",
    language: "Langue",
    notifications: "Notifications",
    save: "Enregistrer",
    cancel: "Annuler",
    email: "Email",
    password: "Mot de passe",
    name: "Nom",
    welcomeBack: "Bon retour",
    createAccount: "Créer un compte",
    forgotPassword: "Mot de passe oublié?",
    alreadyHaveAccount: "Vous avez déjà un compte?",
    signIn: "Se connecter",
    signUp: "S'inscrire",
    errorOccurred: "Une erreur s'est produite",
    success: "Succès",
    loading: "Chargement...",
    logoutConfirmation: "Êtes-vous sûr de vouloir vous déconnecter ?",
    logoutSuccess: "Déconnexion réussie",
    logoutSuccessMessage: "Vous avez été déconnecté de votre compte",
    switchedToAgriculture: "Passé en mode Agriculture",
    switchedToHousehold: "Passé en mode Ménage",
    switchToAgriculture: "Passer en mode Agriculture",
    switchToHousehold: "Passer en mode Ménage",
    dashboardUpdated: "Votre tableau de bord et vos aperçus ont été mis à jour.",
    toggleSidebar: "Basculer la barre latérale",
    toggleTheme: "Basculer le thème",
    appName: "AQUAGENIUS",
    logoAlt: "Logo AquaGenius",
    days: {
      sun: "Dim",
      mon: "Lun",
      tue: "Mar",
      wed: "Mer",
      thu: "Jeu",
      fri: "Ven",
      sat: "Sam"
    },
    assistant: "Assistant IA",
    typeMessage: "Tapez un message...",
    sending: "Envoi",
    error: "Erreur",
    errorMessage: "Désolé, je n'ai pas pu traiter votre demande. Veuillez réessayer plus tard.",
    weather: {
      currentConditions: "Conditions actuelles",
      tomorrowForecast: "Prévisions pour demain",
      temperature: "Température",
      humidity: "Humidité",
      rainfall: "Précipitations",
      irrigationAdvice: "Conseils d'irrigation",
      adjustSchedule: "Ajuster le programme",
      showForecast: "Voir les prévisions",
      getEfficiencyTips: "Obtenir des conseils d'efficacité",
      normalConditions: "Conditions normales. Suivez le programme d'irrigation habituel.",
      highTemperature: "Température élevée détectée. Envisagez une irrigation en soirée pour minimiser l'évaporation.",
      highHumidity: "Humidité élevée détectée. Réduisez l'irrigation pour prévenir le développement de champignons.",
      currentRain: "Il pleut actuellement. Pas besoin d'irrigation.",
      expectedRain: "Pluie prévue demain. Envisagez de retarder l'irrigation."
    },
    locationNeeded: "Accès à la localisation nécessaire",
    locationDescription: "Activez l'accès à la localisation pour obtenir des recommandations personnalisées basées sur la météo",
    enableLocation: "Activer la localisation",
    locationDisabled: "Accès à la localisation désactivé",
    typing: "L'assistant écrit"
  },
  dashboard: {
    waterUsage: "Consommation d'eau",
    totalSaved: "Total économisé",
    savings: "Économies",
    efficiency: "Efficacité",
    weeklyReport: "Rapport hebdomadaire",
    tipOfDay: "Conseil du jour",
    leaks: "Fuites potentielles",
    insights: "Aperçus",
    todayUsage: "Consommation d'aujourd'hui",
    compareToYesterday: "Par rapport à hier",
    household: {
      title: "Tableau de bord de l'eau domestique",
      description: "Surveillez votre consommation d'eau domestique, suivez les économies et recevez des conseils d'optimisation basés sur l'IA.",
      dailyUsage: "Consommation quotidienne",
      monthlyUsage: "Consommation mensuelle",
      efficiencyScore: "Score d'efficacité",
      usageTrends: "Tendances de consommation d'eau"
    },
    agriculture: {
      title: "Tableau de bord de l'eau agricole",
      description: "Surveillez votre utilisation d'eau agricole, suivez l'efficacité de l'irrigation et recevez des recommandations spécifiques aux cultures.",
      dailyIrrigation: "Irrigation quotidienne",
      monthlyIrrigation: "Irrigation mensuelle",
      irrigationEfficiency: "Efficacité de l'irrigation",
      irrigationTrends: "Tendances d'irrigation"
    }
  },
  settings: {
    title: "Paramètres",
    account: "Compte",
    notifications: "Notifications",
    devices: "Appareils",
    privacy: "Confidentialité",
    billing: "Facturation",
    help: "Aide & Support",
    profileInfo: "Informations du profil",
    updateDetails: "Mettre à jour vos informations",
    pwdChange: "Changer votre mot de passe",
    notifPrefs: "Préférences de notification",
    connectedDevices: "Appareils connectés",
    privacySettings: "Paramètres de confidentialité",
    billingInfo: "Informations de facturation",
    faq: "Questions fréquentes",
    languageSettings: "Paramètres de langue",
    chooseLanguage: "Choisissez votre langue et direction du texte",
    selectLanguage: "Sélectionner la langue",
    textDirection: "Direction du texte",
    textDirectionAuto: "La direction du texte est automatiquement définie selon la langue sélectionnée",
    rightToLeft: "De droite à gauche",
    leftToRight: "De gauche à droite",
    current: "Actuel",
    profileInformation: "Informations du profil",
    updateAccountDetails: "Mettre à jour vos informations",
    waterUsageAlerts: "Alertes d'utilisation de l'eau",
    dailyUsageSummary: "Résumé d'utilisation quotidienne",
    dailyUsageSummaryDesc: "Recevoir un résumé quotidien de votre utilisation de l'eau",
    manageNotifications: "Gérer les notifications que vous recevez",
    manageDevices: "Gérer vos appareils de surveillance de l'eau",
    mainWaterMeter: "Mètre d'eau principal",
    connectedSince: "Connecté depuis le {{date}}"
  },
  chatbot: {
    placeholder: "Posez-moi n'importe quelle question sur l'économie d'eau...",
    suggestions: [
      "Comment réduire ma consommation d'eau ?",
      "Ai-je des fuites ?",
      "Montrez-moi des conseils d'économie d'eau",
      "Quelle est ma tendance de consommation d'eau ?"
    ],
    welcome: "Bonjour ! Je suis AquaGenius AI. Comment puis-je vous aider à économiser de l'eau aujourd'hui ?",
    suggestedQuestions: "Questions suggérées :",
    voiceRecording: {
      started: "Enregistrement vocal démarré",
      stopped: "Enregistrement vocal arrêté",
      completed: "Reconnaissance vocale terminée"
    },
    responses: {
      usage: "Selon vos données, vous utilisez environ 20% plus d'eau dans la cuisine que les ménages similaires. Envisagez d'installer un aérateur de robinet à faible débit pour économiser jusqu'à 2650 litres par an.",
      leak: "J'ai remarqué un schéma inhabituel d'utilisation d'eau entre 2h et 4h du matin ces trois derniers jours. Cela pourrait indiquer une fuite dans la salle de bain. Voulez-vous que je vous fournisse des étapes de diagnostic ?",
      plan: "J'ai créé un plan d'approvisionnement en eau optimisé pour vous ! Déplacer l'arrosage du jardin aux heures matinales peut économiser jusqu'à 30% d'eau grâce à une évaporation réduite. Voulez-vous voir le rapport d'optimisation complet ?",
      general: [
        "Saviez-vous que réparer un robinet qui fuit peut économiser jusqu'à 11 000 litres par an ? Je peux vous aider à identifier les fuites potentielles dans votre maison.",
        "Selon votre modèle d'utilisation, la consommation d'eau peut être réduite de 15% grâce à des douches plus courtes. Voulez-vous des conseils sur les habitudes de douche efficaces ?",
        "Votre arrosage de jardin semble inefficace par rapport aux ménages similaires. Envisagez d'installer un système d'irrigation goutte à goutte pour économiser jusqu'à 80% d'eau.",
        "Je remarque que vous utilisez beaucoup d'eau pendant les heures de pointe. Déplacer certaines activités aux heures creuses peut faire économiser de l'argent et réduire la pression sur les systèmes d'eau."
      ]
    },
    ui: {
      assistant: "Assistant AquaGenius",
      openChat: "Ouvrir l'assistant de chat",
      minimize: "Réduire",
      maximize: "Agrandir",
      close: "Fermer"
    }
  },
  insights: {
    title: "IA Aperçus et Conseils",
    waterTips: "Conseils d'économie d'eau",
    analysis: "Analyse de l'eau",
    waterAnalysis: "Analyse de la consommation d'eau",
    exportReport: "Exporter le Rapport",
    shareInsights: "Partager les Aperçus",
    setSchedule: "Définir l'horaire",
    setAlert: "Définir l'alerte",
    viewDetails: "Voir les détails",
    downloadData: "Télécharger les données",
    viewGuide: "Voir le guide",
    setReminder: "Définir un rappel",
    learnMore: "En savoir plus",
    getAlerts: "Obtenir des alertes",
    weather: {
      temperature: "Température",
      humidity: "Humidité",
      rainfall: "Précipitations",
      waterEfficiency: "Efficacité de l'eau",
      feelsLike: "Ressenti",
      last24Hours: "Dernières 24 heures",
      basedOnConditions: "Basé sur les conditions actuelles",
      forecast: "Prévisions sur 5 jours",
      waterManagement: "Conseils de gestion de l'eau",
      recommendations: "Recommandations",
      conditions: {
        high: "Élevé",
        low: "Bas",
        normal: "Normal"
      }
    },
    agricultureTips: {
      irrigation: "Planification intelligente de l'irrigation",
      soil: "Gestion de l'humidité du sol",
      bestTime: "Meilleur moment d'irrigation",
      soilMoisture: "Rétention d'humidité du sol",
      waterStress: "Risque de stress hydrique",
      times: {
        early: "Tôt le matin ou tard le soir",
        mid: "Milieu de matinée"
      },
      status: {
        good: "Bon",
        monitor: "À surveiller",
        high: "Élevé",
        low: "Faible"
      }
    },
    householdTips: {
      daily: "Conservation quotidienne de l'eau",
      smart: "Utilisation intelligente de l'eau",
      indoor: "Humidité intérieure",
      garden: "Arrosage du jardin",
      conservation: "Conservation de l'eau",
      status: {
        dehumidifier: "Envisager un déshumidificateur",
        normal: "Plage normale",
        skip: "Sauter aujourd'hui",
        proceed: "Procéder selon le programme",
        important: "Important",
        standard: "Mesures standard"
      }
    },
    agricultureDescription: "Recommandations d'irrigation intelligentes basées sur les données météorologiques",
    householdDescription: "Conseils d'économie d'eau pour votre maison",
    exportSuccess: "Rapport exporté avec succès",
    exportError: "Échec de l'exportation du rapport",
    shareTitle: "Aperçus de Gestion de l'Eau AQUA",
    shareText: "Découvrez ces aperçus de gestion de l'eau d'AQUA !",
    shareSuccess: "Aperçus partagés avec succès",
    shareError: "Échec du partage des aperçus",
    copySuccess: "Lien copié dans le presse-papiers",
    error: {
      title: "Erreur de Chargement des Aperçus",
      description: "Échec du chargement des données météorologiques. Veuillez réessayer plus tard."
    },
    waterAdvice: {
      agriculture: {
        heavyRain: "Fortes précipitations détectées. Pas d'irrigation nécessaire aujourd'hui.",
        highTempLowHumidity: "Température élevée et faible humidité. Envisagez l'irrigation goutte à goutte pour minimiser les pertes d'eau.",
        rainTomorrow: "Pluie importante prévue demain. Retardez l'irrigation si possible.",
        highHumidity: "Niveaux d'humidité élevés. Surveillez les maladies fongiques et réduisez l'irrigation.",
        normal: "Conditions normales. Suivez le programme d'irrigation habituel."
      },
      household: {
        rainCollection: "Bon jour pour collecter l'eau de pluie pour le jardin.",
        hotDay: "Journée chaude en perspective. Envisagez d'arroser tôt le matin ou tard le soir.",
        rainTomorrow: "Pluie prévue demain. Reportez l'arrosage des plantes extérieures.",
        normal: "Conditions normales. Suivez le programme d'arrosage habituel."
      }
    }
  },
};

// Arabic translations
export const ar = {
  common: {
    dashboard: "لوحة التحكم",
    analytics: "التحليلات",
    leaderboard: "لوحة المتصدرين",
    profile: "الملف الشخصي",
    settings: "الإعدادات",
    chatbot: "مساعد الذكاء الاصطناعي",
    login: "تسجيل الدخول",
    logout: "تسجيل الخروج",
    register: "التسجيل",
    household: "المنزل",
    agriculture: "الزراعة",
    darkMode: "الوضع المظلم",
    lightMode: "الوضع الفاتح",
    language: "اللغة",
    notifications: "الإشعارات",
    save: "حفظ",
    cancel: "إلغاء",
    email: "البريد الإلكتروني",
    password: "كلمة المرور",
    name: "الاسم",
    welcomeBack: "مرحبًا بعودتك",
    createAccount: "إنشاء حساب",
    forgotPassword: "نسيت كلمة المرور؟",
    alreadyHaveAccount: "هل لديك حساب بالفعل؟",
    signIn: "تسجيل الدخول",
    signUp: "التسجيل",
    errorOccurred: "حدث خطأ",
    success: "نجاح",
    loading: "جاري التحميل...",
    logoutConfirmation: "هل أنت متأكد أنك تريد تسجيل الخروج؟",
    logoutSuccess: "تم تسجيل الخروج بنجاح",
    logoutSuccessMessage: "تم تسجيل خروجك من حسابك",
    switchedToAgriculture: "تم التبديل إلى وضع الزراعة",
    switchedToHousehold: "تم التبديل إلى وضع المنزل",
    switchToAgriculture: "التبديل إلى وضع الزراعة",
    switchToHousehold: "التبديل إلى وضع المنزل",
    dashboardUpdated: "تم تحديث لوحة التحكم والرؤى الخاصة بك.",
    toggleSidebar: "تبديل الشريط الجانبي",
    toggleTheme: "تبديل المظهر",
    appName: "أكواجينيوس",
    logoAlt: "شعار أكواجينيوس",
    days: {
      sun: "الأحد",
      mon: "الإثنين",
      tue: "الثلاثاء",
      wed: "الأربعاء",
      thu: "الخميس",
      fri: "الجمعة",
      sat: "السبت"
    },
    assistant: "مساعد الذكاء الاصطناعي",
    typeMessage: "أكتب رسالة...",
    sending: "جاري الإرسال",
    error: "خطأ",
    errorMessage: "آسف، لم أتمكن من معالجة طلبك. يرجى المحاولة مرة أخرى لاحقاً.",
    weather: {
      currentConditions: "الظروف الحالية",
      tomorrowForecast: "توقعات الغد",
      temperature: "درجة الحرارة",
      humidity: "الرطوبة",
      rainfall: "هطول الأمطار",
      irrigationAdvice: "نصائح الري",
      adjustSchedule: "تعديل الجدول",
      showForecast: "عرض التوقعات",
      getEfficiencyTips: "الحصول على نصائح الكفاءة",
      normalConditions: "ظروف عادية. اتبع جدول الري المعتاد.",
      highTemperature: "تم رصد درجة حرارة مرتفعة. يُنصح بالري في المساء لتقليل التبخر.",
      highHumidity: "تم رصد رطوبة عالية. قلل الري لمنع نمو الفطريات.",
      currentRain: "تمطر حالياً. لا حاجة للري.",
      expectedRain: "من المتوقع هطول الأمطار غداً. يُنصح بتأجيل الري."
    },
    locationNeeded: "مطلوب الوصول إلى الموقع",
    locationDescription: "قم بتمكين الوصول إلى الموقع للحصول على توصيات مخصصة بناءً على الطقس",
    enableLocation: "تمكين الموقع",
    locationDisabled: "تم تعطيل الوصول إلى الموقع",
    typing: "المساعد يكتب"
  },
  dashboard: {
    waterUsage: "استهلاك المياه",
    totalSaved: "إجمالي التوفير",
    savings: "التوفير",
    efficiency: "الكفاءة",
    weeklyReport: "التقرير الأسبوعي",
    tipOfDay: "نصيحة اليوم",
    leaks: "تسريبات محتملة",
    insights: "رؤى",
    todayUsage: "استهلاك اليوم",
    compareToYesterday: "مقارنة بالأمس",
    household: {
      title: "لوحة تحكم المياه المنزلية",
      description: "راقب استهلاك المياه المنزلية، تتبع التوفير، واحصل على نصائح تحسين مدعومة بالذكاء الاصطناعي.",
      dailyUsage: "الاستهلاك اليومي",
      monthlyUsage: "الاستهلاك الشهري",
      efficiencyScore: "درجة الكفاءة",
      usageTrends: "اتجاهات استهلاك المياه"
    },
    agriculture: {
      title: "لوحة تحكم المياه الزراعية",
      description: "راقب استخدام المياه الزراعية، تتبع كفاءة الري، واحصل على توصيات خاصة بالمحاصيل.",
      dailyIrrigation: "الري اليومي",
      monthlyIrrigation: "الري الشهري",
      irrigationEfficiency: "كفاءة الري",
      irrigationTrends: "اتجاهات الري"
    }
  },
  settings: {
    title: "الإعدادات",
    account: "الحساب",
    notifications: "الإشعارات",
    devices: "الأجهزة",
    privacy: "الخصوصية",
    billing: "الفواتير",
    help: "المساعدة والدعم",
    profileInfo: "معلومات الملف الشخصي",
    updateDetails: "تحديث معلومات حسابك",
    pwdChange: "تغيير كلمة المرور",
    notifPrefs: "تفضيلات الإشعارات",
    connectedDevices: "الأجهزة المتصلة",
    privacySettings: "إعدادات الخصوصية",
    billingInfo: "معلومات الفواتير",
    faq: "الأسئلة الشائعة",
    languageSettings: "إعدادات اللغة",
    chooseLanguage: "اختر لغتك المفضلة واتجاه النص",
    selectLanguage: "اختر اللغة",
    textDirection: "اتجاه النص",
    textDirectionAuto: "يتم تحديد اتجاه النص تلقائياً بناءً على اللغة المختارة",
    rightToLeft: "من اليمين إلى اليسار",
    leftToRight: "من اليسار إلى اليمين",
    current: "الحالي",
    profileInformation: "معلومات الملف الشخصي",
    updateAccountDetails: "تحديث معلومات حسابك",
    waterUsageAlerts: "إشعارات استهلاك المياه",
    dailyUsageSummary: "ملخص استهلاك اليوم",
    dailyUsageSummaryDesc: "احصل على ملخص استهلاك اليوم من استهلاك المياه",
    manageNotifications: "إدارة الإشعارات التي تتلقاها",
    manageDevices: "إدارة الأجهزة التي تستخدمها لsurveillance المياه",
    mainWaterMeter: "ميتر المياه الرئيسي",
    connectedSince: "متصل منذ {{date}}"
  },
  chatbot: {
    placeholder: "اسألني أي شيء عن توفير المياه...",
    suggestions: [
      "كيف يمكنني تقليل استهلاك المياه؟",
      "هل لدي تسريبات؟",
      "أرني نصائح لتوفير المياه",
      "ما هو اتجاه استهلاكي للمياه؟"
    ],
    welcome: "مرحباً! أنا AquaGenius AI. كيف يمكنني مساعدتك في توفير المياه اليوم؟",
    suggestedQuestions: "أسئلة مقترحة:",
    voiceRecording: {
      started: "بدأ التسجيل الصوتي",
      stopped: "توقف التسجيل الصوتي",
      completed: "اكتمل التعرف الصوتي"
    },
    responses: {
      usage: "بناءً على بياناتك، أنت تستخدم حوالي 20% مياه أكثر في المطبخ مقارنة بالمنازل المماثلة. فكر في تركيب موفر مياه للصنبور لتوفير ما يصل إلى 2650 لتر سنوياً.",
      leak: "لاحظت نمطاً غير معتاد لاستخدام المياه بين الساعة 2-4 صباحاً خلال الأيام الثلاثة الماضية. قد يشير هذا إلى وجود تسرب في الحمام. هل تريد مني تقديم خطوات التشخيص؟",
      plan: "لقد أنشأت خطة مياه محسنة لك! نقل ري الحديقة إلى ساعات الصباح يمكن أن يوفر حتى 30% من المياه بسبب انخفاض التبخر. هل تريد رؤية تقرير التحسين الكامل؟",
      general: [
        "هل تعلم أن إصلاح صنبور يسرب يمكن أن يوفر حتى 11000 لتر سنوياً؟ يمكنني مساعدتك في تحديد التسريبات المحتملة في منزلك.",
        "بناءً على نمط استخدامك، يمكن تقليل استهلاك المياه بنسبة 15% من خلال الاستحمام لفترات أقصر. هل تريد بعض النصائح حول عادات الاستحمام الفعالة؟",
        "يبدو أن ري حديقتك غير فعال مقارنة بالمنازل المماثلة. فكر في تركيب نظام ري بالتنقيط لتوفير ما يصل إلى 80% من المياه.",
        "ألاحظ أنك تستخدم الكثير من المياه خلال ساعات الذروة. نقل بعض الأنشطة إلى ساعات خارج الذروة يمكن أن يوفر المال ويقلل الضغط على أنظمة المياه."
      ]
    },
    ui: {
      assistant: "مساعد AquaGenius",
      openChat: "فتح مساعد المحادثة",
      minimize: "تصغير",
      maximize: "تكبير",
      close: "إغلاق"
    }
  },
  insights: {
    title: "رؤى وتوصيات الذكاء الاصطناعي",
    waterTips: "نصائح توفير المياه",
    analysis: "تحليل المياه",
    waterAnalysis: "تحليل استهلاك المياه",
    exportReport: "تصدير التقرير",
    shareInsights: "مشاركة الرؤى",
    setSchedule: "تحديد الجدول",
    setAlert: "تعيين التنبيه",
    viewDetails: "عرض التفاصيل",
    downloadData: "تحميل البيانات",
    viewGuide: "عرض الدليل",
    setReminder: "تعيين تذكير",
    learnMore: "معرفة المزيد",
    getAlerts: "الحصول على التنبيهات",
    weather: {
      temperature: "درجة الحرارة",
      humidity: "الرطوبة",
      rainfall: "هطول الأمطار",
      waterEfficiency: "كفاءة المياه",
      feelsLike: "الإحساس كأنها",
      last24Hours: "آخر 24 ساعة",
      basedOnConditions: "بناءً على الظروف الحالية",
      forecast: "توقعات 5 أيام",
      waterManagement: "نصائح إدارة المياه",
      recommendations: "التوصيات",
      conditions: {
        high: "مرتفع",
        low: "منخفض",
        normal: "طبيعي"
      }
    },
    agricultureTips: {
      irrigation: "جدولة الري الذكي",
      soil: "إدارة رطوبة التربة",
      bestTime: "أفضل وقت للري",
      soilMoisture: "احتفاظ التربة بالرطوبة",
      waterStress: "خطر الإجهاد المائي",
      times: {
        early: "الصباح الباكر أو المساء المتأخر",
        mid: "منتصف الصباح"
      },
      status: {
        good: "جيد",
        monitor: "مراقبة عن كثب",
        high: "مرتفع",
        low: "منخفض"
      }
    },
    householdTips: {
      daily: "الحفاظ اليومي على المياه",
      smart: "الاستخدام الذكي للمياه",
      indoor: "الرطوبة الداخلية",
      garden: "ري الحديقة",
      conservation: "الحفاظ على المياه",
      status: {
        dehumidifier: "النظر في استخدام مزيل الرطوبة",
        normal: "نطاق طبيعي",
        skip: "تخطي اليوم",
        proceed: "المتابعة حسب الجدول",
        important: "مهم",
        standard: "إجراءات قياسية"
      }
    },
    agricultureDescription: "توصيات الري الذكية بناءً على بيانات الطقس",
    householdDescription: "نصائح لتوفير المياه في منزلك",
    exportSuccess: "تم تصدير التقرير بنجاح",
    exportError: "فشل في تصدير التقرير",
    shareTitle: "رؤى إدارة المياه AQUA",
    shareText: "تحقق من رؤى إدارة المياه هذه من AQUA!",
    shareSuccess: "تمت مشاركة الرؤى بنجاح",
    shareError: "فشل في مشاركة الرؤى",
    copySuccess: "تم نسخ الرابط إلى الحافظة",
    error: {
      title: "خطأ في تحميل الرؤى",
      description: "فشل في تحميل بيانات الطقس. يرجى المحاولة مرة أخرى لاحقاً."
    },
    waterAdvice: {
      agriculture: {
        heavyRain: "تم رصد هطول أمطار غزيرة. تخطي الري اليوم.",
        highTempLowHumidity: "درجة حرارة مرتفعة ورطوبة منخفضة. فكر في الري بالتنقيط لتقليل فقد المياه.",
        rainTomorrow: "من المتوقع هطول أمطار غزيرة غداً. تأخير الري إذا أمكن.",
        highHumidity: "مستويات رطوبة عالية. مراقبة الأمراض الفطرية وتقليل الري.",
        normal: "ظروف طبيعية. اتبع جدول الري المعتاد."
      },
      household: {
        rainCollection: "يوم مناسب لجمع مياه الأمطار لاستخدام الحديقة.",
        hotDay: "يوم حار في الطريق. فكر في ري النباتات في الصباح الباكر أو في المساء المتأخر.",
        rainTomorrow: "من المتوقع هطول الأمطار غداً. تأجيل ري النباتات الخارجية.",
        normal: "ظروف طبيعية. اتبع جدول الري المعتاد."
      }
    }
  },
};

// Get all translations
const translations = { en, fr, ar };

type RecursiveKeyOf<TObj extends Record<string, any>> = {
  [TKey in keyof TObj & (string | number)]: TObj[TKey] extends Record<string, any>
    ? `${TKey}` | `${TKey}.${RecursiveKeyOf<TObj[TKey]>}`
    : `${TKey}`;
}[keyof TObj & (string | number)];

export type TranslationKey = keyof typeof en;
export type TranslationPath = RecursiveKeyOf<typeof en>;

export function getTranslation(
  language: 'en' | 'fr' | 'ar',
  path: TranslationPath
): string {
  const keys = path.split('.');
  let value: any = translations[language];
  
  for (const key of keys) {
    if (value === undefined) return path;
    value = value[key];
  }
  
  return value || path;
}

export default translations;
